<template>
  <CheckinsAutomationsTemplate
    :data-auto-testid="CHECKINS_SCHEDULES_TABLE_WRAPPER_TEST_ID"
    :data-testid="CHECKINS_SCHEDULES_TABLE_WRAPPER_TEST_ID"
    :has-items="!isEmptySchedules"
    class="ci-Wrapper"
    icon="alarm"
  >
    <template #title>
      {{ t('reminder_schedules.title') }}
    </template>
    <template #subtitle>
      {{ t('reminder_schedules.subtitle') }}
    </template>

    <div class="ci-Wrapper_Content">
      <AppTable
        v-if="!isEmptySchedules"
        :columns="TABLE_COLUMNS"
        :data="sortedData"
        :data-auto-testid="CHECKINS_SCHEDULES_TABLE_TEST_ID"
        :data-testid="CHECKINS_SCHEDULES_TABLE_TEST_ID"
        :disable-user-select="false"
        :hover-row="tableHoverRow"
        :loading="loaders.table"
        :row-test-id="CHECKINS_SCHEDULES_TABLE_ROW_TEST_ID"
        border-on-last-row
        no-border
        offset-left="var(--cat-content-left-padding)"
        offset-right="var(--cat-content-right-padding)"
        type="primary-next"
      >
        <template #loading>
          <CheckinsTableLoader />
        </template>

        <template #header-cell="{ column }">
          <TableHeaderCellWithSorting
            v-model:sort-by="sortBy"
            :additional-string-sorting-columns="[SCHEDULE_NAME]"
            :column="column"
          >
            <span v-if="column.key === REMINDER_FULL_NAME" class="ci-HeaderReminderCell">
              {{ column.title }}
            </span>
          </TableHeaderCellWithSorting>
        </template>

        <template #cell="{ columnKey, item, index }">
          <template v-if="columnKey === SCHEDULE_NAME">
            <div
              :data-auto-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              :data-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              class="ci-CheckinsCell_Name ci-CheckinsCell_Link"
              @click="editSchedule(item)"
            >
              <span class="oboard-truncated-text">
                {{ item[NAME] }}
              </span>
            </div>
          </template>

          <template v-if="columnKey === FREQUENCY_FULL_NAME">
            <div
              :data-auto-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              :data-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              class="ci-CheckinsCell"
            >
              <span class="oboard-truncated-text">
                {{ item[FREQUENCY_FULL_NAME] }}
              </span>
            </div>
          </template>

          <template v-if="columnKey === REMINDER_FULL_NAME">
            <div
              :data-auto-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              :data-testid="`${CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID}-${columnKey}`"
              class="ci-CheckinsCell ci-CheckinsCell-reminder"
            >
              <span class="oboard-truncated-text">
                {{ item[REMINDER_FULL_NAME] }}
              </span>
            </div>
          </template>

          <template v-if="columnKey === ACTION">
            <DropdownMenu
              :data-auto-testid="CHECKINS_SCHEDULES_TABLE_ROW_MENU_TEST_ID"
              :data-testid="CHECKINS_SCHEDULES_TABLE_ROW_MENU_TEST_ID"
              :items="MENU_ITEM_ACTIONS"
              :offset="[0, 0]"
              position="bottom-end"
              type="default-next"
              @close="tableHoverRow = -1"
              @open="tableHoverRow = index"
              @item-click="onMenuActionsClick($event, item)"
            >
              <template #activator="{ isOpened }">
                <AppButton
                  :class="{ 'ci-ActionsButton-active': isOpened }"
                  :data-auto-testid="CHECKINS_SCHEDULES_TABLE_ROW_MENU_BUTTON_TEST_ID"
                  :data-testid="CHECKINS_SCHEDULES_TABLE_ROW_MENU_BUTTON_TEST_ID"
                  class="ci-ActionsButton"
                  icon="more-next"
                  size="sm"
                  type="tertiary-next"
                />
              </template>
            </DropdownMenu>
          </template>
        </template>
        <template #footer>
          <AppInfoMessage
            v-if="isLimitExceeded"
            :data-auto-testid="CHECKINS_SCHEDULES_LIMIT_MESSAGE_TEST_ID"
            :data-testid="CHECKINS_SCHEDULES_LIMIT_MESSAGE_TEST_ID"
            :type="INFO_MESSAGE_TYPES.WARNING"
            class="ci-InfoWarningText"
          >
            {{ t('reminder_schedules.reached_schedule_limit') }}
          </AppInfoMessage>
          <AppTableCreateButton
            v-else
            :data-auto-testid="CHECKINS_SCHEDULES_CREATE_BUTTON_TEST_ID"
            :data-testid="CHECKINS_SCHEDULES_CREATE_BUTTON_TEST_ID"
            class="ci-CreateReminderButton"
            full-width
            icon-name="plus-next"
            @click="createSchedule"
          >
            {{ t('reminder_schedules.create_button') }}
          </AppTableCreateButton>
        </template>
      </AppTable>

      <SettingsPagesEmptyState v-if="isEmptySchedules" class="ci-EmptyState" no-divider>
        <template #hero>
          <AppRiveScene
            autoplay
            file-src="https://static.oboard.io/no-schedules-hero.riv"
            height="320"
            loop
            sync-with-modal-session
            width="480"
          >
            <template #fallback>
              <AppIcon class="ci-EmptyHero" height="160" icon-name="no-checkins-hero" width="142" />
            </template>
          </AppRiveScene>
        </template>

        <template #action>
          <AppButtonNext
            :data-auto-testid="CHECKINS_SCHEDULES_CREATE_BUTTON_TEST_ID"
            :data-testid="CHECKINS_SCHEDULES_CREATE_BUTTON_TEST_ID"
            :size="BUTTON_SIZES_NEXT.SM"
            :variant="BUTTON_VARIANTS.TERTIARY"
            icon-before="plus-next"
            @click="createSchedule"
          >
            {{ t('reminder_schedules.create_button') }}
          </AppButtonNext>
        </template>
      </SettingsPagesEmptyState>
    </div>
  </CheckinsAutomationsTemplate>

  <portal to="modal-windows">
    <ScheduleModal
      :opened="isShowScheduleModal"
      :schedule-id="scheduleIdForEdit"
      @on-close="closeScheduleModal"
      @reload-schedules="onReloadSchedules"
      @create-alert-from-reference="emit('create-alert-from-reference', $event)"
    />

    <AppDeleteModal
      :keyword="KEYWORD"
      :loading="loaders.deleteSchedule"
      :show="isShowDeleteScheduleModal"
      :title="$t('reminder_schedules.delete.title')"
      keypath="reminder_schedules.delete.confirm_message"
      @on-close="onDeleteScheduleModalClose"
      @on-confirm="onDeleteScheduleConfirmed"
    >
      <template #i18n-t:scheduleName>
        {{ scheduleForDelete[SCHEDULE_ENTITY_KEYS.NAME] }}
      </template>
    </AppDeleteModal>
  </portal>
</template>

<script setup>
import { isEmpty, isObject } from 'lodash'
import { computed, nextTick, onMounted, ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

import SchedulesApiHandler from '@/api/schedules'
import { tracker } from '@/tracking/amplitude'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { useCheckinTableHelpers } from '@/utils/check-ins/schedule-composables'
import {
  FREQUENCIES_BY_TYPE_ID,
  SCHEDULE_LIMIT,
  SCHEDULE_NOTIFICATION_CHANNELS
} from '@/utils/check-ins/schedules-helpers'
import { isAllUsersStrategySelected } from '@/utils/check-ins/schedules-participants'
import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import {
  NOTIFICATION_CHANNEL_ENTITY_KEYS,
  SCHEDULE_ENTITY_KEYS,
  SCHEDULE_PARTICIPANTS_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { KEYWORD } from '@/utils/helpers'
import { ascSortValue, descSortValue } from '@/utils/sort-options'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

import {
  CHECKINS_SCHEDULES_CREATE_BUTTON_TEST_ID,
  CHECKINS_SCHEDULES_LIMIT_MESSAGE_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_ROW_MENU_BUTTON_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_COLUMN_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_ROW_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_WRAPPER_TEST_ID,
  CHECKINS_SCHEDULES_TABLE_ROW_MENU_TEST_ID
} from '@/components/check-ins/jest-helpers'
import ScheduleModal from '@/components/check-ins/ScheduleModal'
import TableHeaderCellWithSorting from '@/components/dashboard/TableHeaderCellWithSorting'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDeleteModal from '@/components/ui/AppDeleteModal/AppDeleteModal'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppRiveScene from '@/components/ui/AppRiveScene/AppRiveScene'
import AppTableCreateButton from '@/components/ui/AppTableCreateButton'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'
import CheckinsTableLoader from '@/components/ui/SkeletonLoaders/CheckinsTableLoader'
import AppTable from '@/components/ui/Table/AppTable'
import CheckinsAutomationsTemplate from '@/views/workspaces/settings/CheckinsAutomationsTemplate'

const { NAME } = SCHEDULE_ENTITY_KEYS

defineOptions({
  name: 'CheckInsSchedules'
})

const emit = defineEmits({
  'create-alert-from-reference': v => isObject(v) && !isEmpty(v)
})

const { t } = useI18n()

const { SCHEDULE_NAME, FREQUENCY_FULL_NAME, ACTION, REMINDER_FULL_NAME } = TABLE_COLUMNS_KEYS

const tableHoverRow = ref(-1)
const schedulesData = ref([])

const isLimitExceeded = computed(() => schedulesData.value.length >= SCHEDULE_LIMIT)

const TABLE_COLUMNS = [
  {
    title: t('schedule.schedule_name'),
    key: SCHEDULE_NAME,
    width: 'auto',
    sortable: true
  },

  {
    title: t('schedule.frequency'),
    key: FREQUENCY_FULL_NAME,
    width: 220
  },
  {
    title: t('reminder_schedules.reminder'),
    key: REMINDER_FULL_NAME,
    width: 220
  },
  {
    key: ACTION,
    width: 24
  }
]

// const DEFAULT_FILTERS_VALUES = {
//   [SEARCH_STRING]: ''
// }

// const filtersValues = ref({
//   ...cloneDeep(DEFAULT_FILTERS_VALUES)
// })

const loaders = ref({
  table: false,
  deleteSchedule: false
})

const isInitialDataLoaded = ref(false)

const fetchSchedules = async () => {
  try {
    loaders.value.table = true
    const api = new SchedulesApiHandler()
    // const result = await api.fetchSchedules({
    //   ...filtersValues.value
    //   // [FREQUENCIES_IDS]: getSelectWithSelectAllApiParameter(filtersValues.value[FREQUENCIES_IDS])
    // })

    const result = await api.fetchSchedules()

    setSchedulesData(result)
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.table = false
  }
}

const setSchedulesData = data => {
  schedulesData.value = data.map(item => {
    const { frequencyFullName, reminderFullName } = useCheckinTableHelpers({
      schedule: item
    })

    return {
      ...item,
      [FREQUENCY_FULL_NAME]: frequencyFullName,
      [REMINDER_FULL_NAME]: reminderFullName
    }
  })
}

onMounted(async () => {
  await fetchSchedules()
  isInitialDataLoaded.value = true
})

const isEmptySchedules = computed(() => {
  return !loaders.value.table && isEmpty(schedulesData.value) && isInitialDataLoaded.value
})

const isShowScheduleModal = ref(false)

const createSchedule = () => {
  isShowScheduleModal.value = true
}

const scheduleIdForEdit = ref(null)

const closeScheduleModal = () => {
  isShowScheduleModal.value = false
  scheduleIdForEdit.value = null
}

const editSchedule = ({ id }) => {
  scheduleIdForEdit.value = id
  isShowScheduleModal.value = true
}

const onReloadSchedules = async trackingPayload => {
  await fetchSchedules()

  if (trackingPayload) {
    const participants = isAllUsersStrategySelected(
      trackingPayload[SCHEDULE_ENTITY_KEYS.PARTICIPANTS][
        SCHEDULE_PARTICIPANTS_ENTITY_KEYS.STRATEGY_TYPE_ID
      ]
    )
      ? 'all'
      : 'selected'

    const selectedNotificationsChannelIds = trackingPayload[
      SCHEDULE_ENTITY_KEYS.NOTIFICATION_CHANNEL_IDS
    ].map(({ typeId }) => typeId)

    const CHANNELS_BY_TYPE_ID = Object.values(SCHEDULE_NOTIFICATION_CHANNELS).reduce(
      (acc, channel) => {
        acc[channel[NOTIFICATION_CHANNEL_ENTITY_KEYS.VALUE]] = channel
        return acc
      },
      {}
    )

    const channel = selectedNotificationsChannelIds
      .map(id => CHANNELS_BY_TYPE_ID[id]?.[NOTIFICATION_CHANNEL_ENTITY_KEYS.TRACKING_LABEL])
      .filter(Boolean)

    tracker.logEvent(trackingPayload.eventName, {
      frequency: FREQUENCIES_BY_TYPE_ID.getFrequencyByTypeId(
        trackingPayload[SCHEDULE_ENTITY_KEYS.FREQUENCY_TYPE_ID]
      )[SCHEDULE_ENTITY_KEYS.NAME],
      participants,
      channel,
      [SCHEDULE_ENTITY_KEYS.NAME]: trackingPayload[SCHEDULE_ENTITY_KEYS.NAME],
      total_schedules: schedulesData.value.length
    })
  }
}

const isShowDeleteScheduleModal = ref(false)
const scheduleForDelete = ref({})

const onDeleteScheduleModalClose = () => {
  isShowDeleteScheduleModal.value = false
  scheduleForDelete.value = {}
}

const onDeleteScheduleConfirmed = async () => {
  loaders.value.deleteSchedule = true
  const api = new SchedulesApiHandler()

  try {
    const { id } = scheduleForDelete.value
    await api.deleteSchedule({
      id
    })
    await onScheduleDeleted()
  } catch (error) {
    handleError({ error })
  } finally {
    loaders.value.deleteSchedule = false
  }
}

const onScheduleDeleted = async () => {
  await onReloadSchedules()

  tracker.logEvent('check-in schedule deleted', {
    [SCHEDULE_ENTITY_KEYS.NAME]: scheduleForDelete.value[NAME],
    total_schedules: schedulesData.value.length
  })

  onDeleteScheduleModalClose()
}

const onDeleteSchedule = async schedule => {
  scheduleForDelete.value = schedule
  await nextTick()
  isShowDeleteScheduleModal.value = true
}

const MENU_ITEM_ACTIONS = [
  {
    name: ACTIONS_KEYS.EDIT,
    title: 'action.edit',
    icon: 'edit-next'
  },
  {
    name: ACTIONS_KEYS.REMOVE,
    title: 'action.delete',
    icon: 'delete-next',
    color: 'var(--grade-low-color-next)'
  }
]

const onMenuActionsClick = (name, item) => {
  if (name === ACTIONS_KEYS.EDIT) {
    editSchedule(item)
  }
  if (name === ACTIONS_KEYS.REMOVE) {
    onDeleteSchedule(item)
  }
}

const sortBy = ref({
  key: SCHEDULE_NAME,
  value: null
})

const sortedData = computed(() => {
  if (isEmpty(schedulesData.value)) {
    return []
  }

  const { value } = toValue(sortBy)

  if (value && value === ascSortValue) {
    return schedulesData.value.toSorted((a, b) => a[SCHEDULE_NAME].localeCompare(b[SCHEDULE_NAME]))
  }

  if (value && value === descSortValue) {
    return schedulesData.value.toSorted((a, b) => b[SCHEDULE_NAME].localeCompare(a[SCHEDULE_NAME]))
  }

  return schedulesData.value
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/app-table-helpers';

.ci-Wrapper_Content {
  width: 100%;
  max-width: $page-width-lg;
  gap: 24px;
}

.ci-CheckinsCell_Link {
  color: $primary-color-next;
  &:hover {
    text-decoration: underline;
  }
}

.ci-CheckinsCell_Name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: fw('semi-bold');
  cursor: pointer;
  padding-right: 8px;
}

.ci-InfoWarningText {
  margin-top: 10px;
  padding: 0 $page-right-padding 10px var(--cat-content-left-padding);
}

.ci-ActionsButton {
  &-active {
    background-color: $dark-2;
    color: $white;
  }
  @extend %app-table-hidden-items;
}

.ci-HeaderReminderCell {
  padding-right: 12px;
}

.ci-CheckinsCell {
  padding-right: 8px;
  display: flex;

  &-reminder {
    padding-right: 12px;

    > * {
      width: 100%;
    }
  }
}

.ci-EmptyHero {
  margin-bottom: 49px;
}

.ci-EmptyState {
  --spes-padding: 12px 0;
}

.ci-CreateReminderButton {
  border-bottom-left-radius: calc($border-radius-md - 1px);
  border-bottom-right-radius: calc($border-radius-md - 1px);
  --padding-left: var(--cat-content-left-padding);
}
</style>
